import * as React from 'react'
import { Check } from 'lucide-react'
import { Textarea } from '@/components/ui/textarea'
import { CALL_FEEDBACK_AUTOSAVE_DELAY_MS } from '@/config/constants'
import { useUpdateCallFeedbackMutation } from '@/features/calls/hooks/useUpdateCallFeedbackMutation'
import { useMountEffect } from '@/hooks/useMountEffect'

function normalizeFeedback(value: string): string | null {
  return value.trim() || null
}

interface CallFeedbackFieldProps {
  callId: string
  initialFeedback: string | null | undefined
}

export function CallFeedbackField({
  callId,
  initialFeedback,
}: CallFeedbackFieldProps) {
  const fieldId = React.useId()
  const [draft, setDraft] = React.useState(initialFeedback ?? '')
  const updateFeedback = useUpdateCallFeedbackMutation(callId)
  const { mutate, isPending, isError, isSuccess } = updateFeedback
  const timerRef = React.useRef<ReturnType<typeof setTimeout> | null>(null)
  const draftRef = React.useRef(draft)
  draftRef.current = draft
  const lastSavedRef = React.useRef<string | null>(
    normalizeFeedback(initialFeedback ?? '')
  )
  const lastRequestedRef = React.useRef<string | null>(lastSavedRef.current)
  const hasUnsavedChanges = normalizeFeedback(draft) !== lastSavedRef.current

  const saveFeedback = React.useCallback(
    (nextDraft: string) => {
      const normalized = normalizeFeedback(nextDraft)
      if (
        normalized === lastSavedRef.current ||
        normalized === lastRequestedRef.current
      ) {
        return
      }

      lastRequestedRef.current = normalized
      mutate(normalized, {
        onSuccess: (response) => {
          lastSavedRef.current = response.data.call_feedback
        },
        onError: () => {
          lastRequestedRef.current = lastSavedRef.current
        },
      })
    },
    [mutate]
  )
  const saveFeedbackRef = React.useRef(saveFeedback)
  saveFeedbackRef.current = saveFeedback

  useMountEffect(() => {
    return () => {
      if (timerRef.current !== null) {
        clearTimeout(timerRef.current)
        timerRef.current = null
      }
      saveFeedbackRef.current(draftRef.current)
    }
  })

  const scheduleSave = React.useCallback(
    (nextDraft: string) => {
      if (timerRef.current !== null) clearTimeout(timerRef.current)
      timerRef.current = setTimeout(() => {
        saveFeedback(nextDraft)
      }, CALL_FEEDBACK_AUTOSAVE_DELAY_MS)
    },
    [saveFeedback]
  )

  const flushSave = React.useCallback(() => {
    if (timerRef.current !== null) {
      clearTimeout(timerRef.current)
      timerRef.current = null
    }
    saveFeedback(draft)
  }, [draft, saveFeedback])

  return (
    <div>
      <div className="mb-2 flex items-center justify-between gap-3">
        <p className="text-muted-foreground text-xs font-medium tracking-wide">
          Call Feedback
        </p>
        <p
          className={
            isError
              ? 'text-destructive text-xs'
              : 'text-muted-foreground text-xs'
          }
          aria-live="polite">
          {isPending ? (
            'Saving...'
          ) : isError ? (
            'Save failed'
          ) : isSuccess && !hasUnsavedChanges ? (
            <span className="inline-flex items-center gap-1 text-emerald-600">
              <Check className="h-3 w-3" aria-hidden="true" />
              Saved
            </span>
          ) : null}
        </p>
      </div>
      <Textarea
        id={fieldId}
        // The visible "Call Feedback" title above is a <p>, not a <label>,
        // so attach the accessible name directly via aria-label. Screen readers
        // and React Testing Library's getByLabelText both pick this up.
        aria-label="Call Feedback"
        value={draft}
        placeholder="Add call feedback..."
        className="min-h-24 resize-y"
        onBlur={flushSave}
        onChange={(event) => {
          const nextDraft = event.target.value
          setDraft(nextDraft)
          scheduleSave(nextDraft)
        }}
      />
    </div>
  )
}
